import { ExclamationCircleFilled, QuestionCircleFilled } from '@ant-design/icons';
import { Button, Card, ConfigProvider, Space, Typography } from 'antd';
import i18n from 'i18n';
import { darkTheme } from 'Provider/themes/dark';
import { lightTheme } from 'Provider/themes/llight';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import './IConfirmModal.css';

const { Text, Title } = Typography;

interface IConfirmProps {
  title: string;
  message: string;
  yesAlertFunc: () => void;
  noAlertFunc?: () => void;
  type?: 'delete' | 'confirm';
  buttonText?: string;
  cancelText?: string;
}

export const IConfirmModal = (props: IConfirmProps) => {
  const { title, message, yesAlertFunc, noAlertFunc, type = 'delete', buttonText, cancelText } = props;
  const isDelete = type === 'delete';
  // Translate "Cancel" via i18next at the moment the dialog is shown. Callers can
  // still override by passing `cancelText`. Falls back to "Cancel" if the key
  // isn't found (e.g., before i18n init).
  // The cast is needed because i18next's bundled type declarations in this
  // project are broken (see node_modules/i18next/typescript/t.d.ts).
  const cancelLabel = cancelText ?? ((i18n as any).t('dashboard:installation.cancel') as string) ?? 'Cancel';
  // react-confirm-alert renders outside the app's ConfigProvider tree, so the
  // global theme isn't inherited. We re-attach the active theme by reading
  // data-theme="dark" from <html> at the moment the dialog is shown.
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const theme = isDark ? darkTheme : lightTheme;

  return confirmAlert({
    overlayClassName: "custom-overlay-blur",
    customUI: ({ onClose }) => {
      return (
        <ConfigProvider theme={theme}>
          <div className="custom-ui-wrapper">
            <Card
              style={{
                width: 420,
                borderRadius: 16,
                boxShadow: isDark
                  ? '0 10px 40px 0 rgba(0, 0, 0, 0.6)'
                  : '0 6px 16px 0 rgba(0, 0, 0, 0.08)',
                border: isDark ? '1px solid #2E3138' : 'none',
                background: isDark ? '#0B172B' : undefined,
              }}
              styles={{
                body: { background: isDark ? '#0B172B' : undefined },
              }}
            >
              <div style={{ display: 'flex', gap: '16px' }}>
                {/* Dynamic Icon */}
                {isDelete ? (
                  <ExclamationCircleFilled style={{ fontSize: '24px', color: '#faad14' }} />
                ) : (
                  <QuestionCircleFilled style={{ fontSize: '24px', color: '#1677ff' }} />
                )}

                <div>
                  <Title level={5} style={{ margin: 0, marginBottom: 8, color: isDark ? '#FFFFFF' : undefined }}>
                    {title}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '14px', color: isDark ? '#CBD5E1' : undefined }}>
                    {message}
                  </Text>
                </div>
              </div>

              <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
                <Space size="middle">
                  <Button
                    type="text"
                    shape="round"
                    size="large"
                    onClick={() => {
                      if (noAlertFunc) noAlertFunc();
                      onClose();
                    }}
                    style={{ fontWeight: 500, color: isDark ? '#CBD5E1' : undefined }}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="primary"
                    danger={isDelete}
                    shape="round"
                    size="large"
                    onClick={() => {
                      yesAlertFunc();
                      onClose();
                    }}
                    style={{ paddingLeft: 30, paddingRight: 30 }}
                  >
                    {isDelete ? `${buttonText ? buttonText : 'Delete'}` : `${buttonText ? buttonText : 'Delete'}`}
                  </Button>
                </Space>
              </div>
            </Card>
          </div>
        </ConfigProvider>
      );
    }
  });
};